// This is due Axios internal interfaces 👆
import { useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import usePushDataLayerEvent from '../../utils/google-analytics.util';
import { showModal } from '../../store/slices/generalSlice';
import storageService from '../../services/storage.service';
import { IAssistance, IAssistanceResponse, ISavePosition } from '../../models/assistance.model';
import { IPolicyResponse, getPolicy, getPolicyOnDemand } from '../../api/auth/getPolicy';
import {
  getAssistanceInformation,
  getAssistances,
  getCraneLocation,
  getFilePhone,
  getIntervention,
  requestACall,
  requestCrane,
} from '../../api/assistance/assistance';
import AdacaAPI from '../../api';
import { IUseAssistanceProps, AssistanceDeleteBody } from './use-assistance-types';

const useAssistance = ({
  requestCraneBody,
  authPolicyBody,
  requestCraneLocationParams,
  intervention,
  assistanceInformationParams,
  requestAssistancesFlag,
}: IUseAssistanceProps) => {
  //
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { pushDataLayerEvent } = usePushDataLayerEvent();
  const authPolicyBodyRef = useRef(authPolicyBody);
  authPolicyBodyRef.current = authPolicyBody;

  const requestAssistance = useMutation({
    mutationFn: (assistanceData: IAssistance) => AdacaAPI.post<IAssistanceResponse>('/assistance', assistanceData),
  });

  const requestSavePosition = useMutation({
    mutationFn: (positionData: ISavePosition) =>
      AdacaAPI.post<void>(`/Assistance/AIA/${positionData.identifier}/${positionData.file}/save-geoposition`, {
        latitude: positionData.position.latitude,
        longitude: positionData.position.longitude,
        externalRoad: positionData.position.externalRoad,
        undergroundParking: positionData.position.undergroundParking,
      }),
  });

  interface IErrorCodeResponse {
    codigo: string | undefined;
  }

  // Query de obtención de póliza (URL de SMS)
  const requestAuthPolicy = useQuery<IPolicyResponse, AxiosError>({
    queryKey: ['policyData', { authPolicyBody }],
    queryFn: () => {
      if (authPolicyBody) {
        const { onConfirm: _, ...body } = authPolicyBody;
        return getPolicy(body);
      }
      throw new Error('Error');
    },
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
  });

  // Manejo de respuesta exitosa en llamada de chequeo de póliza (URL de SMS)
  useEffect(() => {
    if (requestAuthPolicy.isSuccess && requestAuthPolicy.data) {
      pushDataLayerEvent('entrar_sin_cuenta', requestAuthPolicy.data, 'confirmacion_de_poliza');
      const body = authPolicyBodyRef.current;
      if (body && body.onConfirm) body.onConfirm(requestAuthPolicy.data);
    }
  }, [requestAuthPolicy.isSuccess, requestAuthPolicy.data, pushDataLayerEvent]);

  // Manejo de errores en llamada de chequeo de póliza (URL de SMS)
  useEffect(() => {
    if (requestAuthPolicy.isError && requestAuthPolicy.error) {
      const data = requestAuthPolicy.error.response?.data as IErrorCodeResponse;
      const errorCode = data?.codigo as string;
      const callMeBackErrorCodes = ['412', '413', '414', '417'];
      pushDataLayerEvent(
        'error',
        {
          codigo_error: errorCode || null,
          mensaje_error: (requestAuthPolicy.error?.response?.data as any)?.message || null,
          tipo_error: (requestAuthPolicy.error?.response?.data as any)?.type || null,
        },
        'chequeo_poliza_sms',
      );
      if (errorCode && callMeBackErrorCodes.includes(errorCode)) {
        dispatch(showModal({ name: 'error-confirm' }));
      } else {
        dispatch(showModal({ name: 'confirm-data' }));
      }
    }
  }, [requestAuthPolicy.isError, requestAuthPolicy.error, dispatch, pushDataLayerEvent]);

  // Query de obtención de póliza a demanda (URL pública de compañías)
  const requestAuthPolicyOnDemand = useQuery<IPolicyResponse, AxiosError>({
    queryKey: ['policyDataOnDemand', { authPolicyBody }],
    queryFn: async () => {
      if (authPolicyBody) {
        const { onConfirm: _, identifier: __, ...body } = authPolicyBody;
        return getPolicyOnDemand(body);
      }
      throw new Error('Error');
    },
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
  });

  // Manejo de respuesta exitosa en llamada de chequeo de póliza a demanda (URL pública de compañías)
  useEffect(() => {
    if (requestAuthPolicyOnDemand.isSuccess && requestAuthPolicyOnDemand.data) {
      pushDataLayerEvent('entrar_sin_cuenta', requestAuthPolicyOnDemand.data, 'confirmacion_de_poliza');
      const body = authPolicyBodyRef.current;
      if (body && body.onConfirm) body.onConfirm(requestAuthPolicyOnDemand.data);
    }
  }, [requestAuthPolicyOnDemand.isSuccess, requestAuthPolicyOnDemand.data, pushDataLayerEvent]);

  // Manejo de errores en llamada de chequeo de póliza a demanda (URL pública de compañías)
  useEffect(() => {
    if (requestAuthPolicyOnDemand.isError && requestAuthPolicyOnDemand.error) {
      const error = requestAuthPolicyOnDemand.error;
      const data = error.response?.data as IErrorCodeResponse;
      const errorCode = data?.codigo as string;
      pushDataLayerEvent(
        'error',
        {
          codigo_error: errorCode || null,
          mensaje_error: (error?.response?.data as any)?.message || null,
          tipo_error: (error?.response?.data as any)?.type || null,
        },
        'chequeo_poliza_demanda',
      );

      switch (errorCode) {
        // Póliza no encontrada o no válida
        case '411':
          dispatch(showModal({ name: 'confirm-data' }));
          break;
        // Identificador no válido o ya utilizado
        case '412':
        case '413':
        case '414':
          dispatch(showModal({ name: 'error-confirm' }));
          break;
        default:
          dispatch(showModal({ name: 'error-confirm' }));
          break;
      }
    }
  }, [requestAuthPolicyOnDemand.isError, requestAuthPolicyOnDemand.error, dispatch, pushDataLayerEvent]);

  const requestCranes = useQuery({
    queryKey: ['getCranes', { requestCraneBody }],
    queryFn: () => requestCrane(requestCraneBody),
    enabled: !!requestCraneBody,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const cancelAssistance = useMutation({
    mutationFn: async (assistanceDelete: AssistanceDeleteBody) => {
      const oldCanceledAssistances = await storageService.getItem<string[]>('canceledAssistances');
      const newCanceledAssistances = [
        ...(Array.isArray(oldCanceledAssistances) ? oldCanceledAssistances : []),
        assistanceDelete.identifier,
      ];
      await storageService.setItem('canceledAssistances', newCanceledAssistances);
      return AdacaAPI.delete(`/Assistance`, { params: assistanceDelete });
    },
  });

  const requestCraneLocation = useQuery({
    queryKey: ['getCraneLocation', { requestCraneLocationParams }],
    queryFn: () => getCraneLocation(requestCraneLocationParams),
    enabled: !!(
      requestCraneLocationParams &&
      requestCraneLocationParams?.file &&
      requestCraneLocationParams?.intervention
    ),
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: 10000,
  });

  const requestAssistanceInformation = useQuery({
    queryKey: ['getAssistanceInformation', { assistanceInformationParams }],
    queryFn: () =>
      getAssistanceInformation(
        assistanceInformationParams?.identifier,
        assistanceInformationParams?.insuranceCompany,
        'Assistance',
      ),
    enabled: !!(assistanceInformationParams?.identifier && assistanceInformationParams?.insuranceCompany),
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: 60000,
  });

  const requestIntervention = useQuery({
    queryKey: ['getIntervention', { intervention }],
    queryFn: () => getIntervention(intervention),
    enabled: !!intervention,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const requestFilePhone = useQuery({
    queryKey: ['getFilePhone', { intervention }],
    queryFn: () => getFilePhone(intervention),
    enabled: !!intervention,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const requestAssistances = useQuery({
    queryKey: ['getAssistances'],
    queryFn: () => getAssistances(),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!requestAssistancesFlag,
  });

  const requestACallMutation = useMutation({
    mutationFn: (intervention: string) => requestACall(intervention),
  });

  return {
    requestAssistance,
    requestSavePosition,
    requestCranes,
    requestAuthPolicy,
    cancelAssistance,
    queryClient,
    requestCraneLocation,
    requestIntervention,
    requestAuthPolicyOnDemand,
    requestACallMutation,
    requestAssistances,
    requestAssistanceInformation,
    requestFilePhone,
  };
};

export default useAssistance;
